import AttractMode from './control_modes/attract'
import ModeSwitcher from '../../render/mode-switcher'
import MouseMode from './control_modes/mouse'
import KeyboardMode from './control_modes/keyboard'
import {RenderEventDispatcher} from './event-dispatcher'
import {Box3, Vector3} from 'three'
import type {PerspectiveCamera, WebGLRenderer} from 'three'
import {logger} from '../../render/setup-globals'
import type ThreeDViewer from './viewer'
import type Renderable from './renderable'
import type {RenderableObject} from '3d'
import type {NullRenderer} from './null-renderer'
import octicons from '@primer/octicons'

type ControlModes = 'attract' | 'mouse'
type ControlEvent = {
  type: 'tick'
  tick: {
    data: number
  }
}

const enableAutoRotateIcon = octicons['play'].toSVG()
// Access this icon via a string index to avoid type mismatches in Octicons typings.
const disableAutoRotateIcon = (octicons as Record<string, {toSVG(): string}>)['square-circle']!.toSVG()
const enableAutoRotateLabel = 'Start auto-rotation'
const disableAutoRotateLabel = 'Stop auto-rotation'
const autoRotateButtonHtmlId = 'auto-rotation-button'
/**
 * Controls provides the camera controls interface to the 3D object viewer.
 */
class Controls extends RenderEventDispatcher<ControlEvent> {
  viewer: ThreeDViewer
  renderer: WebGLRenderer | NullRenderer
  camera: PerspectiveCamera
  // Standard quanta of rotation. Roughly about one degree.
  // Used by control modes to increment the orbit position.
  orbitIncrement: number = Math.PI / 180
  center: Vector3 = new Vector3(0, 0, 0)
  container: HTMLElement | null = null
  modes: {
    attract: AttractMode
    mouse: MouseMode
    keyboard: KeyboardMode
  }
  autoRotateEnabled: boolean

  constructor(viewer: ThreeDViewer) {
    super()

    this.viewer = viewer
    this.viewer.addEventListener('added', this.updateCenter)
    this.viewer.addEventListener('tick', this.update)

    this.renderer = this.viewer.renderer
    this.camera = this.viewer.camera!

    this.container = this.renderer.domElement

    const distance = this.orbitDistance()
    this.camera.position.set(0, -(distance / 2), distance).setLength(distance)

    this.modes = {
      attract: new AttractMode(this, true),
      mouse: new MouseMode(this, false, {onUse: this.#activateMouseMode, onRelease: this.#activateAttractMode}),
      keyboard: new KeyboardMode(this, false, {
        onUse: this.#activateKeyboardMode,
        onRelease: this.#activateAttractMode,
      }),
    }

    this.autoRotateEnabled = false

    this.initModeMenu()
    this.initControlPanel()
    this.updateCenter()
    const autoRotateButton = document.getElementById(autoRotateButtonHtmlId)
    if (autoRotateButton) {
      autoRotateButton.innerHTML = enableAutoRotateIcon
      autoRotateButton.ariaLabel = enableAutoRotateLabel
      autoRotateButton.ariaPressed = 'false'
      autoRotateButton.addEventListener('click', this.#toggleAutoRotate)
    }
  }

  // Utils
  containerEl() {
    if (!this.container) {
      this.container = this.renderer.domElement
    }
    return this.container
  }

  selectMode = (mode: ControlModes) => {
    logger.debug(`Switching to ${mode} mode`)
    const objects = this.viewer.objects as Record<string, Renderable>
    for (const name of Object.keys(objects)) {
      const obj = objects[name]
      obj.switchMode(mode)
    }
  }

  #toggleAutoRotate = (e: MouseEvent) => {
    this.autoRotateEnabled = !this.autoRotateEnabled
    const autoRotateButton = e.currentTarget as HTMLElement
    autoRotateButton.innerHTML = this.autoRotateEnabled ? disableAutoRotateIcon : enableAutoRotateIcon
    const label = this.autoRotateEnabled ? disableAutoRotateLabel : enableAutoRotateLabel
    autoRotateButton.ariaLabel = label
    autoRotateButton.ariaPressed = String(this.autoRotateEnabled)
    if (this.autoRotateEnabled) {
      this.#activateAttractMode()
    } else {
      this.modes.attract.deactivate()
    }
  }

  #activateMouseMode = () => {
    for (const name of Object.keys(this.modes)) {
      const mode = this.modes[name as ControlModes]
      mode.deactivate()
    }
    this.modes.mouse.activate()
  }

  #activateKeyboardMode = () => {
    for (const name of Object.keys(this.modes)) {
      const mode = this.modes[name as ControlModes]
      mode.deactivate()
    }
    this.modes.keyboard.activate()
  }

  #activateAttractMode = () => {
    // We only want this to fire if the user has explicitly allowed
    // auto-rotation
    if (!this.autoRotateEnabled) return
    for (const name of Object.keys(this.modes)) {
      const mode = this.modes[name as ControlModes]
      mode.deactivate()
    }

    this.modes.attract.activate()
  }

  initModeMenu = () => {
    return new ModeSwitcher<ControlModes>(this.selectMode)
  }

  initControlPanel = () => {
    // Left panel: rotate + auto-rotate controls, positioned bottom-left.
    const leftPanel = document.createElement('div')
    leftPanel.className = '3d-control-panel left'

    // Rotate controls
    const rotateControls = document.createElement('div')
    rotateControls.className = '3d-control-group rotate'

    const rotateUpBtn = this.createControlButton(
      'up',
      'Rotate Up',
      () => this.orbitUp(this.orbitIncrement * 10),
      'undo',
    )
    const rotateLeftBtn = this.createControlButton(
      'left',
      'Rotate Left',
      () => this.orbitLeft(this.orbitIncrement * 10),
      'undo',
    )
    const rotateRightBtn = this.createControlButton(
      'right',
      'Rotate Right',
      () => this.orbitRight(this.orbitIncrement * 10),
      'undo',
    )
    const rotateDownBtn = this.createControlButton(
      'down',
      'Rotate Down',
      () => this.orbitDown(this.orbitIncrement * 10),
      'undo',
    )

    // Append in visual reading order (top-to-bottom, left-to-right). The
    // auto-rotate button occupies the center slot, so it is inserted between
    // the left and right rotate buttons.
    rotateControls.appendChild(rotateUpBtn)
    rotateControls.appendChild(rotateLeftBtn)

    // Relocate the auto-rotate button into the center of the rotate cross.
    const autoRotateButton = document.getElementById(autoRotateButtonHtmlId)
    if (autoRotateButton) {
      autoRotateButton.classList.add('center')
      rotateControls.appendChild(autoRotateButton)
    }

    rotateControls.appendChild(rotateRightBtn)
    rotateControls.appendChild(rotateDownBtn)

    leftPanel.appendChild(rotateControls)

    // Right panel: zoom + pan controls, positioned bottom-right.
    const rightPanel = document.createElement('div')
    rightPanel.className = '3d-control-panel right'

    // Combined pan + zoom controls laid out in a single grid so the zoom
    // buttons sit to the right of the pan up/down buttons (like the mermaid
    // viewer).
    const panControls = document.createElement('div')
    panControls.className = '3d-control-group pan'

    const panUpBtn = this.createControlButton('up', 'Pan Up', () => this.translateUp(10), 'chevron-up')
    const zoomInBtn = this.createControlButton('zoom-in', 'Zoom In', () => this.changeZoom(-0.1), 'zoom-in')
    const panLeftBtn = this.createControlButton('left', 'Pan Left', () => this.translateRight(10), 'chevron-left')
    const resetBtn = this.createControlButton('center reset', 'Reset position', () => this.resetView(), 'sync')
    const panRightBtn = this.createControlButton('right', 'Pan Right', () => this.translateLeft(10), 'chevron-right')
    const panDownBtn = this.createControlButton('down', 'Pan Down', () => this.translateDown(10), 'chevron-down')
    const zoomOutBtn = this.createControlButton('zoom-out', 'Zoom Out', () => this.changeZoom(0.1), 'zoom-out')

    // Append in visual reading order (top-to-bottom, left-to-right) so the tab
    // order matches the on-screen grid layout.
    panControls.appendChild(panUpBtn)
    panControls.appendChild(zoomInBtn)
    panControls.appendChild(panLeftBtn)
    panControls.appendChild(resetBtn)
    panControls.appendChild(panRightBtn)
    panControls.appendChild(panDownBtn)
    panControls.appendChild(zoomOutBtn)

    rightPanel.appendChild(panControls)

    // Add the control panels to the container
    const container = this.containerEl()
    if (container && container.parentElement) {
      container.parentElement.style.position = 'relative'
      container.parentElement.appendChild(leftPanel)
      container.parentElement.appendChild(rightPanel)
    }
  }

  createControlButton = (className: string, title: string, onClick: () => void, icon: keyof typeof octicons) => {
    const button = document.createElement('button')
    button.className = `btn ${className}`
    button.innerHTML = octicons[icon].toSVG()
    button.ariaLabel = title
    button.addEventListener('click', onClick)
    return button
  }

  orbitDistance = () => {
    const zoom = 5
    const radius = (object: Renderable) => {
      return (object.geometry.boundingSphere && object.geometry.boundingSphere.radius) ?? 0
    }
    const viewerObjects = (this.viewer.objects ?? {}) as Record<string, Renderable>
    const voKeys = Object.keys(viewerObjects)
    const objectRadii = voKeys.map<number>(name => radius(viewerObjects[name] ?? 0))

    const max = Math.max(...objectRadii)
    if (max > 0) {
      return max * zoom
    } else {
      return 100
    }
  }

  // Restore the camera to its initial orientation and framing, matching the
  // setup performed in the constructor.
  resetView = () => {
    this.updateCenter()
    this.camera.up.set(0, 1, 0)
    const distance = this.orbitDistance()
    this.camera.position.set(0, -(distance / 2), distance).setLength(distance)
  }

  // Yeild to fn after offsetting `object` by `-offeset` first
  // then calling `fn` then adding back the offset vector as
  // `+offset` to `object.position` to make orbital rotations
  // and length scaling of camera position easier.
  withoutOriginOffset(camera: PerspectiveCamera, offset: Vector3, fn: () => void) {
    camera.position.sub(offset)
    fn()
    camera.position.add(offset)
  }

  // Direct camera controls
  orbitRight = (angle: number) => {
    this.withoutOriginOffset(this.camera, this.center, () => {
      const axis = new Vector3(0, 0, 1)
      this.camera.position.applyAxisAngle(axis, angle)
      this.camera.up.applyAxisAngle(axis, angle)
    })
  }

  orbitUp = (angle: number) => {
    this.withoutOriginOffset(this.camera, this.center, () => {
      const axis = this.camera.position.clone().cross(this.camera.up).normalize()
      this.camera.position.applyAxisAngle(axis, angle)
      this.camera.up.applyAxisAngle(axis, angle)
    })
  }

  translateUp = (change: number) => {
    const translation = this.camera.up.clone().normalize().multiplyScalar(change)
    this.camera.position.add(translation)
    this.center.add(translation)
  }

  translateLeft = (change: number) => {
    const up = this.camera.up.clone().normalize()
    const position = this.camera.position.clone().normalize()
    const translation = up.cross(position).multiplyScalar(change)
    this.camera.position.add(translation)
    this.center.add(translation)
  }

  changeZoom = (delta: number) => {
    this.withoutOriginOffset(this.camera, this.center, () => {
      if (delta > 0) {
        this.camera.position.multiplyScalar(1.02)
      } else if (delta < 0) {
        this.camera.position.multiplyScalar(0.98)
      }
    })
  }

  // Sign inverting camera controls
  orbitLeft = (angle: number) => {
    return this.orbitRight(-angle)
  }
  orbitDown = (angle: number) => {
    return this.orbitUp(-angle)
  }
  translateDown = (change: number) => {
    return this.translateUp(-change)
  }
  translateRight = (change: number) => {
    return this.translateLeft(-change)
  }

  // Handlers
  update = () => {
    this.camera.lookAt(this.center)
    this.dispatchEvent({type: 'tick', data: 0})
  }

  updateCenter = () => {
    let bounds: Box3 | undefined = undefined
    this.viewer!.scene!.traverse((o: RenderableObject) => {
      if (o.mesh) {
        return
      }

      if (!o.geometry) {
        return
      }

      const geometry = o.geometry

      if (!geometry.boundingBox) {
        return
      }

      geometry.computeBoundingBox()

      if (!bounds) {
        bounds = geometry.boundingBox.clone()
      } else {
        bounds.expandByPoint(geometry.boundingBox.min)
        bounds.expandByPoint(geometry.boundingBox.max)
      }
    })

    if (bounds) {
      this.center = (bounds as Box3).getCenter(new Vector3()).clone()
    }

    logger.debug('Center updated, now:', this.center)
  }
}

export default Controls
